import React, { useMemo, useState } from "react";
import { type FieldPath, type FieldValues } from "react-hook-form";
import { type GroupBase } from "react-select";
import {
  ReactSelectSyncProps,
  ReactSelectAsyncProps,
  ReactSelectController,
  ReactSelectControllerProps,
} from "@pushpress/shared-components";
import { Separator, Button } from "@pushpress/pushpress-ui";
import { Plus } from "lucide-react";
import {
  usePlanCategoriesQuery,
  usePostPlanCategoryMutation,
} from "../../../apis/subscriptionApi.hooks";
import { toast } from "react-toastify";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { AppointmentType } from "../../../apis/appointmentApi";
import { sortByName } from "../../../utils/sortUtils";
import { useQueryClient } from "@tanstack/react-query";

type PlanCategorySelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends AppointmentType,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = ReactSelectControllerProps<TFieldValues, TName, Option, IsMulti, Group>;

function PlanCategorySelect<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends AppointmentType,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: PlanCategorySelectProps<TFieldValues, TName, Option, IsMulti, Group> &
    (ReactSelectSyncProps | ReactSelectAsyncProps),
) {
  const [inputValue, setInputValue] = useState("");
  const queryClient = useQueryClient();
  const createCategoryMutation = usePostPlanCategoryMutation();
  const { data, refetch } = usePlanCategoriesQuery({
    staleTime: Infinity,
  });

  const options = useMemo(() => {
    if (!data?.data) {
      return [];
    }
    return [...data.data].sort(sortByName) as unknown as Option[];
  }, [data]);

  const handleAddCategory = async () => {
    if (!inputValue.trim()) return;

    try {
      await createCategoryMutation.mutateAsync({ name: inputValue });
      toast.success("Category created successfully", defaultToastOptions);
      setInputValue("");
      await queryClient.invalidateQueries({ queryKey: ["planCategories"] });
      await refetch();
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    }
  };

  const menuAdornment = (
    <>
      <Separator />
      <div className="tw-flex tw-items-center tw-gap-2 tw-p-2 tw-w-full">
        <Button
          variant="ghost"
          onClick={handleAddCategory}
          disabled={!inputValue.trim()}
          className="tw-w-full"
        >
          <Plus className="tw-w-4 tw-h-4 tw-mr-2" />
          <span>Add &quot;{inputValue}&quot; as new category</span>
        </Button>
      </div>
    </>
  );

  return (
    <ReactSelectController
      menuPortalTarget={document.body}
      {...props}
      options={options}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.uuid}
      menuAdornment={menuAdornment}
      onInputChange={(newValue) => setInputValue(newValue)}
      inputValue={inputValue}
      noOptionsMessage={({ inputValue }) =>
        inputValue
          ? `No categories found for "${inputValue}"`
          : "No categories found"
      }
    />
  );
}

export default PlanCategorySelect;
